/**
 * Reviews marquee.
 *
 * Fetches reviews from the proxy, splits them across two rows running opposite
 * ways, and upgrades each row to a swipeable scroll container that still
 * advances on its own — same behaviour as the press bar.
 *
 * Everything is rendered with DOM methods rather than innerHTML: review text is
 * written by customers, so it is never treated as markup.
 *
 * If the section asks for it (data-rating-live), the aggregate line above the
 * rows is refreshed from the same response: the average of the ratings the
 * endpoint returned, and the `count` the payload carries. Liquid has already
 * written believable numbers into that markup, so this only ever overwrites
 * text that is on screen — there is nothing to build and nothing to reflow.
 */
(function () {
  var RESUME_DELAY = 1400;
  var reduced =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function stars(rating) {
    var whole = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    return '★★★★★'.slice(0, whole) + '☆☆☆☆☆'.slice(0, 5 - whole);
  }

  function formatCount(value) {
    // Arabic storefronts get Arabic-Indic digits and the right grouping mark.
    try {
      return value.toLocaleString(document.documentElement.lang || undefined);
    } catch (err) {
      return String(value);
    }
  }

  /**
   * Overwrite the Liquid-rendered score and count with the feed's own numbers.
   * Every step is optional: a missing node, an unrated feed or a count of zero
   * just leaves the typed fallback standing.
   */
  function updateSummary(root, data) {
    if (root.dataset.ratingLive !== '1' || !data) return;

    var summary = root.querySelector('[data-rm-summary]');
    if (!summary) return;

    var rated = (data.reviews || []).filter(function (review) {
      return Number(review.rating) > 0;
    });

    if (rated.length) {
      var total = rated.reduce(function (sum, review) {
        return sum + Number(review.rating);
      }, 0);
      var average = total / rated.length;
      var scale = parseFloat(summary.dataset.rmScale) || 5;
      var score = average.toFixed(1);

      var scoreNode = summary.querySelector('[data-rm-score]');
      if (scoreNode) {
        scoreNode.textContent = score;
        var rating = scoreNode.closest('.reviews-marquee__summary-rating');
        if (rating) rating.hidden = false;
      }

      var pct = Math.max(0, Math.min(100, (average / scale) * 100));
      summary.style.setProperty('--rm-rating-pct', pct.toFixed(2) + '%');

      var starsNode = summary.querySelector('[data-rm-stars]');
      if (starsNode) {
        starsNode.setAttribute('aria-label', score + ' out of ' + scale + ' stars');
      }
    }

    // `count` is every review worth showing, not just the page we were sent.
    var count = Number(data.count);
    var countNode = summary.querySelector('[data-rm-count]');
    if (countNode && count > 0) {
      countNode.textContent = formatCount(count);
      var wrapper = countNode.closest('.reviews-marquee__summary-count');
      if (wrapper) wrapper.hidden = false;
    }
  }

  function buildCard(review) {
    var item = el('div', 'reviews-marquee__item');
    var card = el('div', 'reviews-marquee__card');

    if (review.picture) {
      var img = document.createElement('img');
      img.className = 'reviews-marquee__avatar';
      img.src = review.picture;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      card.appendChild(img);
    } else {
      // Judge.me has no avatars, so fall back to the reviewer's initials.
      card.appendChild(el('span', 'reviews-marquee__avatar', review.initials || '★'));
    }

    var body = el('div', 'reviews-marquee__body');
    var rating = el('div', 'reviews-marquee__stars', stars(review.rating));
    rating.setAttribute('aria-label', (review.rating || 5) + ' out of 5 stars');
    body.appendChild(rating);
    var text = el('p', 'reviews-marquee__text', review.body);
    // Reviews arrive in Arabic and English; let the browser pick direction.
    text.setAttribute('dir', 'auto');
    body.appendChild(text);

    var meta = review.name || '';
    if (review.product_title) meta += ' · ' + review.product_title;
    if (meta) {
      var metaNode = el(
        'p',
        'reviews-marquee__meta' + (review.verified ? ' reviews-marquee__verified' : ''),
        meta
      );
      metaNode.setAttribute('dir', 'auto');
      body.appendChild(metaNode);
    }

    card.appendChild(body);
    item.appendChild(card);
    return item;
  }

  function setupViewport(root, viewport, reverse) {
    var track = viewport.querySelector('.reviews-marquee__track');
    if (!track) return;

    var styles = getComputedStyle(root);
    var copies = parseFloat(styles.getPropertyValue('--rm-copies')) || 3;
    var duration = parseFloat(styles.getPropertyValue('--rm-duration')) || 60;
    if (reverse) duration *= 1.18;

    var groupWidth = 0;
    var paused = false;
    var resumeTimer = null;
    var visible = true;
    var last = 0;
    var carry = 0;

    function measure() {
      groupWidth = track.scrollWidth / copies;
      if (groupWidth > 0 && viewport.scrollLeft < 1) viewport.scrollLeft = groupWidth;
    }

    function wrap() {
      if (!groupWidth) return;
      if (viewport.scrollLeft >= groupWidth * 2) viewport.scrollLeft -= groupWidth;
      else if (viewport.scrollLeft < groupWidth * 0.5) viewport.scrollLeft += groupWidth;
    }

    function hold() {
      paused = true;
      if (resumeTimer) clearTimeout(resumeTimer);
    }

    function release() {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        paused = false;
      }, RESUME_DELAY);
    }

    function frame(now) {
      var dt = last ? (now - last) / 1000 : 0;
      last = now;

      if (!paused && !reduced && visible && groupWidth > 0 && dt > 0 && dt < 0.5) {
        var step = (groupWidth / duration) * dt + carry;
        var whole = Math.trunc(step);
        carry = step - whole;
        if (whole) {
          viewport.scrollLeft += reverse ? -whole : whole;
          wrap();
        }
      }
      requestAnimationFrame(frame);
    }

    ['touchstart', 'pointerdown', 'wheel'].forEach(function (evt) {
      viewport.addEventListener(evt, hold, { passive: true });
    });
    ['touchend', 'touchcancel', 'pointerup', 'pointercancel'].forEach(function (evt) {
      viewport.addEventListener(evt, function () {
        wrap();
        release();
      }, { passive: true });
    });
    viewport.addEventListener('wheel', release, { passive: true });

    if (root.classList.contains('reviews-marquee--pausable')) {
      viewport.addEventListener('mouseenter', hold);
      viewport.addEventListener('mouseleave', release);
      viewport.addEventListener('focusin', hold);
      viewport.addEventListener('focusout', release);
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
      }).observe(root);
    }

    measure();
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(track);
    else window.addEventListener('resize', measure);

    requestAnimationFrame(frame);
  }

  function render(root, reviews) {
    var rows = root.querySelectorAll('.reviews-marquee__row');
    if (!rows.length) return;

    var copies = parseInt(root.dataset.copies, 10) || 3;

    // Alternate reviews between the rows so both read as a fair sample.
    var split = [[], []];
    reviews.forEach(function (review, i) {
      split[i % 2].push(review);
    });

    // One row of reviews is better than two thin ones.
    if (split[1].length < 2) {
      split = [reviews, []];
      rows[1].hidden = true;
    }

    for (var r = 0; r < rows.length; r += 1) {
      var list = split[r];
      if (!list || !list.length) {
        rows[r].hidden = true;
        continue;
      }

      var track = rows[r].querySelector('.reviews-marquee__track');
      track.textContent = '';

      for (var c = 0; c < copies; c += 1) {
        var group = el('div', 'reviews-marquee__group');
        if (c > 0) group.setAttribute('aria-hidden', 'true');
        for (var i = 0; i < list.length; i += 1) {
          var card = buildCard(list[i]);
          if (c > 0) {
            var link = card.querySelector('a');
            if (link) link.tabIndex = -1;
          }
          group.appendChild(card);
        }
        track.appendChild(group);
      }
    }

    root.style.setProperty('--rm-copies', copies);
    root.classList.remove('reviews-marquee__skeleton');
    root.classList.add('reviews-marquee--swipe');

    var viewports = root.querySelectorAll('.reviews-marquee__row:not([hidden]) .reviews-marquee__viewport');
    for (var v = 0; v < viewports.length; v += 1) {
      setupViewport(root, viewports[v], v === 1);
    }
  }

  function load(root) {
    if (root.dataset.reviewsReady) return;
    root.dataset.reviewsReady = '1';

    var endpoint = root.dataset.endpoint;
    if (!endpoint) return;

    var url =
      endpoint +
      (endpoint.indexOf('?') === -1 ? '?' : '&') +
      'limit=' + encodeURIComponent(root.dataset.limit || 40) +
      '&min_rating=' + encodeURIComponent(root.dataset.minRating || 4);

    fetch(url, { credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('Reviews endpoint returned ' + res.status);
        return res.json();
      })
      .then(function (data) {
        try {
          updateSummary(root, data);
        } catch (err) {
          // A bad aggregate must never cost the section its reviews.
          if (window.console) console.warn('[reviews-marquee] summary', err.message);
        }

        var reviews = (data && data.reviews) || [];
        // A static data file cannot honour the query string, so cap here too.
        var cap = parseInt(root.dataset.limit, 10) || 40;
        var floor = parseInt(root.dataset.minRating, 10) || 0;
        reviews = reviews.filter(function (r) { return (r.rating || 5) >= floor; }).slice(0, cap);
        if (!reviews.length) {
          root.hidden = true;
          return;
        }
        render(root, reviews);
      })
      .catch(function (err) {
        // Better an absent section than a row of empty skeletons.
        root.hidden = true;
        if (window.console) console.warn('[reviews-marquee]', err.message);
      });
  }

  function initAll() {
    var roots = document.querySelectorAll('.reviews-marquee[data-endpoint]');
    for (var i = 0; i < roots.length; i += 1) load(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('.reviews-marquee[data-endpoint]');
    if (root) load(root);
  });
})();
